import { memo, useCallback, useMemo } from 'react'
import type { NodeOption } from '../../data/nodeOptions'

interface NodeListItemProps {
  option: NodeOption
  onSelect: (option: NodeOption) => void
}

// Memoized component - prevents re-renders when parent updates with unchanged props
const NodeListItem = memo(function NodeListItem({ option, onSelect }: NodeListItemProps) {
  // Memoized click handler
  const handleClick = useCallback(() => {
    onSelect(option);
  }, [onSelect, option]);

  // Memoized icon render
  const iconElement = useMemo(() => {
    if (option.iconUrl) {
      return (
        <img
          src={option.iconUrl}
          alt={option.title}
          className="w-5 h-5 object-contain"
          onError={(e) => {
            // Fallback to emoji on error - use textContent for security
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            if (target.parentElement) {
              target.parentElement.textContent = option.icon || '📦';
            }
          }}
        />
      );
    }
    return <span>{option.icon}</span>;
  }, [option.iconUrl, option.title, option.icon]);

  return (
    <button
      key={option.id}
      onClick={handleClick}
      className="w-full flex items-center gap-2 px-2.5 py-2 hover:bg-accent rounded-lg transition-colors text-left group"
    >
      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-base">
        {iconElement}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-xs leading-tight">{option.title}</div>
        <div className="text-[10px] text-muted-foreground leading-tight">{option.description}</div>
      </div>
      <div className="flex-shrink-0 text-muted-foreground text-xs opacity-0 group-hover:opacity-100 transition-opacity">→</div>
    </button>
  )
});

export default NodeListItem;
